/**
 * /master/analytics — Master Console: Hero CTA A/B test analytics.
 *
 * Reads /api/analytics/landing-stats and renders:
 *  - KPI cards (total impressions, total clicks, blended CTR)
 *  - Per-variant bar chart (impression + click + CTR)
 *  - Winning variant badge (only when statistical floor met: 20+ impressions)
 *  - Window picker (7d / 30d / 90d) and CTA picker (hero-primary / tenant-hero-primary / all)
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, TrendingUp, Trophy, Eye, MousePointerClick, Filter, Pause, Play } from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VARIANT_PALETTE = ["#C9A24E", "#10B981", "#3B82F6", "#EC4899", "#F59E0B", "#8B5CF6"];

export default function MasterAnalytics() {
  const [windowDays, setWindowDays] = useState(30);
  const [ctaFilter, setCtaFilter] = useState("");
  const [data, setData] = useState(null);
  const [paused, setPaused] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadTick, setReloadTick] = useState(0);

  // Resolve effective CTA for the pause UI (default "hero-primary" so master
  // can still pause when the filter is "All CTAs").
  const effectiveCta = ctaFilter || "hero-primary";

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ days: String(windowDays) });
    if (ctaFilter) params.set("cta_id", ctaFilter);
    axios.get(`${API}/analytics/landing-stats?${params.toString()}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [windowDays, ctaFilter, reloadTick]);

  // Fetch current pause list for the active CTA
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/analytics/variant-resolver/${encodeURIComponent(effectiveCta)}`)
      .then(({ data }) => { if (alive) setPaused(Array.isArray(data?.paused_variants) ? data.paused_variants : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [effectiveCta, reloadTick]);

  const togglePause = async (variantKey) => {
    const next = paused.includes(variantKey)
      ? paused.filter((v) => v !== variantKey)
      : [...paused, variantKey];
    try {
      await axios.put(`${API}/analytics/auto-pause`, { cta_id: effectiveCta, paused_variants: next });
      setPaused(next);
      setReloadTick((t) => t + 1);
    } catch (e) {
      // Surface error inline; revert local state implicitly via reload.
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const variants = useMemo(() => {
    if (!data?.variants) return [];
    return Object.entries(data.variants)
      .map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [data]);

  const totals = useMemo(() => {
    const impr = variants.reduce((s, v) => s + (v.impression || 0), 0);
    const clk = variants.reduce((s, v) => s + (v.click || 0), 0);
    return { impr, clk, ctr: impr ? Math.round((clk / impr) * 10000) / 100 : 0 };
  }, [variants]);

  const maxCtr = Math.max(0.01, ...variants.map((v) => v.ctr || 0));

  return (
    <MasterLayout title="A/B Analytics">
      <div className="space-y-6" data-testid="master-analytics-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-black text-white" data-testid="master-analytics-h1">Hero CTA A/B Analytics</h1>
            <p className="text-[13.5px] mt-1 text-white/60">Real-time funnel of every hero/CTA variant served on the public landing & tenant pages.</p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Filter className="w-4 h-4 text-[#C9A24E]" />
            <CtaPicker value={ctaFilter} onChange={setCtaFilter} />
            <WindowPicker value={windowDays} onChange={setWindowDays} />
          </div>
        </div>

        {loading ? (
          <div data-testid="master-analytics-loading" className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A24E]" /></div>
        ) : error ? (
          <div data-testid="master-analytics-error" className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-red-300 text-[13px]">{error}</div>
        ) : (
          <>
            <KpiCards totals={totals} winner={data?.winner} winnerFloorMet={data?.winner_floor_met} />
            <VariantChart variants={variants} maxCtr={maxCtr} winner={data?.winner} paused={paused} onTogglePause={togglePause} />
            <RawTable variants={variants} winner={data?.winner} paused={paused} />
          </>
        )}
      </div>
    </MasterLayout>
  );
}

function CtaPicker({ value, onChange }) {
  const options = [
    { v: "", l: "All CTAs" },
    { v: "hero-primary", l: "Public landing — hero" },
    { v: "tenant-hero-primary", l: "Tenant landing — hero" },
  ];
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      data-testid="master-analytics-cta-picker"
      className="h-9 rounded-md bg-white/5 border px-3 text-[12.5px] text-white outline-none"
      style={{ borderColor: "rgba(201,162,78,0.25)" }}>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

function WindowPicker({ value, onChange }) {
  return (
    <div data-testid="master-analytics-window" className="inline-flex rounded-md p-0.5 border" style={{ borderColor: "rgba(201,162,78,0.25)" }}>
      {[7, 30, 90].map((d) => {
        const active = value === d;
        return (
          <button key={d} type="button" onClick={() => onChange(d)}
            data-testid={`master-analytics-window-${d}`}
            className="px-3 h-8 text-[11.5px] font-bold tracking-wider rounded transition-colors"
            style={{ backgroundColor: active ? "#C9A24E" : "transparent", color: active ? "#0F0820" : "rgba(255,255,255,0.7)" }}>
            {d}d
          </button>
        );
      })}
    </div>
  );
}

function KpiCards({ totals, winner, winnerFloorMet }) {
  const Cell = ({ icon: Icon, label, value, suffix, color = "#C9A24E", testid }) => (
    <div data-testid={testid} className="rounded-xl border p-4 flex flex-col gap-1.5"
      style={{ backgroundColor: "#1A0E36", borderColor: "rgba(201,162,78,0.18)" }}>
      <div className="flex items-center gap-2 text-[10.5px] font-mono uppercase tracking-[0.18em] text-white/55">
        <Icon className="w-3.5 h-3.5" style={{ color }} /> {label}
      </div>
      <div className="font-display font-black text-3xl tabular-nums" style={{ color: "#FFFFFF" }}>
        {value}{suffix && <span className="text-[16px] ml-1 font-bold" style={{ color }}>{suffix}</span>}
      </div>
    </div>
  );
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="master-analytics-kpis">
      <Cell icon={Eye} label="Impressions" value={fmt(totals.impr)} testid="kpi-impressions" />
      <Cell icon={MousePointerClick} label="Clicks" value={fmt(totals.clk)} testid="kpi-clicks" color="#10B981" />
      <Cell icon={TrendingUp} label="Blended CTR" value={totals.ctr.toFixed(2)} suffix="%" testid="kpi-ctr" color="#3B82F6" />
      <div data-testid="kpi-winner" className="rounded-xl border p-4 flex flex-col gap-1.5"
        style={{ backgroundColor: winner ? "rgba(201,162,78,0.12)" : "#1A0E36", borderColor: winner ? "#C9A24E" : "rgba(201,162,78,0.18)" }}>
        <div className="flex items-center gap-2 text-[10.5px] font-mono uppercase tracking-[0.18em] text-white/55">
          <Trophy className="w-3.5 h-3.5" style={{ color: "#C9A24E" }} /> Winning variant
        </div>
        <div className="flex items-center gap-2">
          <div className="font-display font-black text-3xl" style={{ color: winner ? "#C9A24E" : "#71717A" }}>
            {winner || "—"}
          </div>
          {winner && (
            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded"
              style={{ backgroundColor: winnerFloorMet ? "#10B981" : "#71717A", color: "#0F0820" }}>
              {winnerFloorMet ? "Significant" : "Tentative"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function VariantChart({ variants, maxCtr, winner, paused = [], onTogglePause }) {
  if (!variants.length) {
    return <div data-testid="master-analytics-empty" className="rounded-xl border p-8 text-center text-white/55" style={{ borderColor: "rgba(201,162,78,0.18)", backgroundColor: "#1A0E36" }}>No events recorded for this window. Variants will populate as visitors interact with the public landing.</div>;
  }
  return (
    <div data-testid="master-analytics-chart" className="rounded-xl border p-6"
      style={{ backgroundColor: "#1A0E36", borderColor: "rgba(201,162,78,0.18)" }}>
      <div className="flex items-center justify-between mb-5">
        <div className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-white/55">Variant performance</div>
        {paused.length > 0 && (
          <div data-testid="paused-summary" className="text-[10.5px] font-mono uppercase tracking-[0.18em]" style={{ color: "#F59E0B" }}>
            {paused.length} paused
          </div>
        )}
      </div>
      <div className="space-y-4">
        {variants.map((v, i) => {
          const color = VARIANT_PALETTE[i % VARIANT_PALETTE.length];
          const ctrPct = (v.ctr / maxCtr) * 100;
          const isWinner = v.key === winner;
          const isPaused = paused.includes(v.key);
          return (
            <div key={v.key} data-testid={`variant-row-${v.key}`} className={isPaused ? "opacity-55" : ""}>
              <div className="flex items-center justify-between text-[12.5px] mb-1.5 gap-3 flex-wrap">
                <div className="flex items-center gap-2 font-bold text-white">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                  Variant {v.key}
                  {isWinner && <Trophy className="w-3.5 h-3.5" style={{ color: "#C9A24E" }} />}
                  {isPaused && (
                    <span data-testid={`variant-paused-${v.key}`} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: "rgba(245,158,11,0.2)", color: "#F59E0B" }}>
                      paused
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-white/65 tabular-nums">
                    <span className="text-white font-bold">{v.ctr.toFixed(2)}%</span> CTR
                    <span className="mx-2 opacity-40">·</span>
                    {fmt(v.impression || 0)} impr · {fmt(v.click || 0)} clicks
                  </div>
                  {onTogglePause && (
                    <button type="button" onClick={() => onTogglePause(v.key)}
                      data-testid={`pause-toggle-${v.key}`}
                      className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-1 rounded transition-colors"
                      style={{
                        backgroundColor: isPaused ? "rgba(16,185,129,0.15)" : "rgba(245,158,11,0.15)",
                        color: isPaused ? "#10B981" : "#F59E0B",
                        border: `1px solid ${isPaused ? "rgba(16,185,129,0.4)" : "rgba(245,158,11,0.4)"}`,
                      }}
                      title={isPaused ? "Resume serving this variant" : "Stop serving this variant to new visitors"}>
                      {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                      {isPaused ? "Resume" : "Pause"}
                    </button>
                  )}
                </div>
              </div>
              <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(2, ctrPct)}%`, backgroundColor: color }} />
              </div>
            </div>
          );
        })}
      </div>
      {paused.length === 0 && (
        <p className="text-[11px] mt-5" style={{ color: "rgba(255,255,255,0.45)" }}>
          💡 Click "Pause" on a losing variant to stop the hook from serving it on new page loads. Visitors who already saw it keep their assignment.
        </p>
      )}
    </div>
  );
}

function RawTable({ variants, winner, paused = [] }) {
  return (
    <div data-testid="master-analytics-table" className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: "#1A0E36", borderColor: "rgba(201,162,78,0.18)" }}>
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-left text-[10.5px] font-mono uppercase tracking-[0.18em] text-white/55">
            {["Variant", "Impressions", "Clicks", "CTR"].map((h) => (
              <th key={h} className="px-4 py-3" style={{ borderBottom: "1px solid rgba(201,162,78,0.18)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {variants.map((v) => {
            const isWinner = v.key === winner;
            return (
              <tr key={v.key} data-testid={`raw-row-${v.key}`} className="text-white">
                <td className="px-4 py-3 font-bold flex items-center gap-2">
                  Variant {v.key} {isWinner && <Trophy className="w-3.5 h-3.5" style={{ color: "#C9A24E" }} />}
                </td>
                <td className="px-4 py-3 tabular-nums">{fmt(v.impression || 0)}</td>
                <td className="px-4 py-3 tabular-nums" style={{ color: "#10B981" }}>{fmt(v.click || 0)}</td>
                <td className="px-4 py-3 tabular-nums font-bold" style={{ color: isWinner ? "#C9A24E" : "#FFFFFF" }}>{v.ctr.toFixed(2)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function fmt(n) { return new Intl.NumberFormat("en-US").format(n || 0); }
